import type * as exceljs from 'exceljs'

import type { NumberFormat } from './number-format'
import type { ValueType } from './value-type'

// TODO: add JSDoc examples

/**
 * Represents a single cell in an Excel sheet
 */
export class Cell {
  /**
   * Creates a single {@link Cell} with no content
   */
  public static empty(): Cell
  /**
   * Creates the requested number of {@link Cell}, each with no content
   */
  public static empty(count: number): Cell[]
  public static empty(count?: number): Cell | Cell[] {
    if (count === undefined) {
      return new Cell(null)
    }

    // NOTE: we need to make sure each Cell is a separate instance, which is
    // why we don't just pass Cell.empty() to fill
    return new Array(count).fill(0).map(_ => Cell.empty())
  }

  public readonly value: exceljs.CellValue
  public readonly style: Partial<exceljs.Style>

  public columnSpan?: number
  public valueType?: ValueType
  public numberFormat?: NumberFormat

  constructor(value: exceljs.CellValue) {
    this.style = {}
    this.value = value
    this.style.alignment = {}
  }

  public fontBold(): this {
    this.style.font = {
      ...this.style.font,
      bold: true,
    }
    return this
  }

  public fontItalic(): this {
    this.style.font = {
      ...this.style.font,
      italic: true,
    }
    return this
  }

  public fontColor(color: string | undefined): this {
    this.style.font = {
      ...this.style.font,
      color: { argb: color },
    }

    return this
  }

  public solidBackground(color: string | undefined): this {
    this.style.fill = {
      fgColor: {
        argb: color,
      },
      pattern: 'solid',
      type: 'pattern',
    }

    return this
  }

  public borderBottom(borderBottom: Partial<exceljs.Border> | undefined): this {
    this.style.border ??= {}
    this.style.border.bottom = borderBottom
    return this
  }

  public borderTop(borderTop: Partial<exceljs.Border> | undefined): this {
    this.style.border ??= {}
    this.style.border.top = borderTop
    return this
  }

  public alignment(alignment: Partial<exceljs.Alignment> | undefined): this {
    this.style.alignment = {
      ...this.style.alignment,
      ...alignment,
    }

    return this
  }

  public wrapText(shouldWrap: boolean | undefined): this {
    this.style.alignment = {
      ...this.style.alignment,
      wrapText: shouldWrap,
    }

    return this
  }

  public setValueType(type: ValueType | undefined): this {
    this.valueType = type
    return this
  }

  public fontSize(size: number | undefined): this {
    this.style.font = {
      ...this.style.font,
      size,
    }
    return this
  }

  public setNumberFormat(numberFormat: NumberFormat | undefined): this {
    this.numberFormat = numberFormat
    return this
  }

  /**
   * Merges this cell with the next `span - 1` cells in its row (so `span`
   * is the total number of columns covered, including this cell itself)
   *
   * __IMPORTANT:__ {@link Workbook} positions every cell in a row by its
   * index in that row's cell array, not by how many columns earlier cells
   * in the row have already consumed via their own `columnSpan`. So a
   * spanning cell must be followed by exactly `span - 1` placeholder cells
   * (e.g. {@link Cell.empty}) before the next real cell, or that next
   * cell's merge range will overlap this one's — which exceljs rejects by
   * throwing `Cannot merge already merged cells`, failing the whole
   * workbook. See `room-supporting-information.ts`'s report content
   * generator for a worked example of padding a row this way.
   */
  public setColumnSpan(span: number | undefined): this {
    this.columnSpan = span

    return this
  }
}
